import { useState } from 'react'
import { collectResource } from '@/economy/api'
import { useEconomyStore } from '@/state/useEconomyStore'
import type { WalletInfo, ItemType } from '@/economy/types'
import type { InteractionTarget } from '@/state/useEconomyStore'

interface PanelProps { interaction: InteractionTarget; wallet: WalletInfo | null }

const COLLECT_COOLDOWN = 60000  // 60 seconds cooldown per type
const COLLECT_ITEMS: Array<{ item: ItemType; label: string; qty: number; desc: string }> = [
  { item: 'wool', label: 'Shear Wool', qty: 2, desc: 'Shear the sheep for wool.' },
  { item: 'eggs', label: 'Gather Eggs', qty: 3, desc: 'Collect fresh eggs from the chickens.' },
]

export function PasturePanel(_props: PanelProps) {
  const [cooldowns, setCooldowns] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState<string | null>(null)
  const [lastCollected, setLastCollected] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const setWallet = useEconomyStore((s) => s.setWallet)

  function canCollect(item: string) {
    const lastTime = cooldowns[item] || 0
    return Date.now() - lastTime > COLLECT_COOLDOWN
  }

  function getCooldownRemaining(item: string): number {
    const lastTime = cooldowns[item] || 0
    return Math.max(0, COLLECT_COOLDOWN - (Date.now() - lastTime))
  }

  async function handleCollect(item: ItemType, qty: number) {
    setLoading(item)
    setError(null)
    try {
      const newWallet = await collectResource(item, qty)
      setWallet(newWallet)
      setCooldowns((prev) => ({ ...prev, [item]: Date.now() }))
      setLastCollected(item)
      setTimeout(() => setLastCollected(null), 3000)
    } catch (e: any) {
      setError(e.message || 'Collection failed')
    }
    setLoading(null)
  }

  return (
    <>
      <div className="panel-section">
        <h3 className="panel-section__title">Livestock</h3>
        <div className="panel-row">
          <span className="panel-row__label">Sheep</span>
          <span className="panel-row__value" style={{ color: '#6b8b4a' }}>Healthy</span>
        </div>
        <div className="panel-row">
          <span className="panel-row__label">Chickens</span>
          <span className="panel-row__value" style={{ color: '#6b8b4a' }}>Healthy</span>
        </div>
      </div>

      <div className="panel-section">
        <h3 className="panel-section__title">Collect</h3>
        {error && <div className="panel-error">{error}</div>}
        {COLLECT_ITEMS.map(({ item, label, qty, desc }) => {
          const ready = canCollect(item)
          const remaining = getCooldownRemaining(item)
          const isLoading = loading === item
          const justCollected = lastCollected === item

          return (
            <div key={item}>
              <div className="panel-row">
                <span className="panel-row__label">
                  {label}
                  <span style={{ color: '#8b7355', fontSize: 11, marginLeft: 6 }}>&times;{qty}</span>
                </span>
                <button
                  className="panel-btn"
                  disabled={!ready || isLoading}
                  onClick={() => handleCollect(item, qty)}
                >
                  {isLoading ? 'Collecting...'
                    : justCollected ? 'Collected!'
                    : !ready ? `${Math.ceil(remaining / 1000)}s`
                    : 'Collect'}
                </button>
              </div>
              <p style={{ color: '#8b7355', fontFamily: 'Crimson Text, serif', fontSize: 11, margin: '2px 0 8px' }}>
                {desc}
              </p>
            </div>
          )
        })}
      </div>
    </>
  )
}
